import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import { DatePicker } from '#/shared/ui/date-picker'
import { Input } from '#/shared/ui/input'
import { Label } from '#/shared/ui/label'
import { Skeleton } from '#/shared/ui/skeleton'
import { Textarea } from '#/shared/ui/textarea'

import { METHOD_TYPE_ICON } from '../model/methods'

import { ProofDropzone } from './proof-dropzone'

const METHOD_SKELETON_WIDTHS = ['w-16', 'w-20', 'w-18', 'w-14'] as const

interface OtherMethodFormProps {
  selectedMethod: string | undefined
  onSelectMethod: (method: string) => void
  recordedMethods: ReadonlyArray<string>
  methods: ReadonlyArray<string>
  isMethodsLoading: boolean
  proofFiles: ReadonlyArray<File>
  onAddProofFiles: (files: ReadonlyArray<File>) => void
  onRemoveProofFile: (index: number) => void
  amount: string
  onAmountChange: (next: string) => void
  amountOverLimit: boolean
  chargeTotalLabel: string
  date: Date
  onDateChange: (next: Date) => void
  reference: string
  onReferenceChange: (next: string) => void
}

function MethodPill({
  active,
  recorded,
  label,
  method,
  onClick,
}: {
  active: boolean
  recorded: boolean
  label: string
  method: string
  onClick: () => void
}) {
  const Icon = METHOD_TYPE_ICON[method]
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'relative flex h-8 shrink-0 cursor-pointer items-center gap-1.25 rounded-md border px-3 text-sm transition-colors select-none',
        active
          ? 'border-primary bg-blue-100 text-primary'
          : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50',
      )}
    >
      {Icon && <Icon className="size-4 shrink-0" aria-hidden />}
      {label}
      {recorded && (
        <span
          aria-hidden
          className="absolute -top-1 -right-1 size-2 rounded-full bg-primary ring-2 ring-white"
        />
      )}
    </button>
  )
}

function MethodPillSkeleton({ width }: { width: string }) {
  return (
    <Skeleton
      className={cn('h-8 shrink-0 rounded-md border border-gray-200', width)}
    />
  )
}

function MethodReceivedField({
  methods,
  selectedMethod,
  recordedMethods,
  onSelectMethod,
  isMethodsLoading,
}: {
  methods: ReadonlyArray<string>
  selectedMethod: string | undefined
  recordedMethods: ReadonlyArray<string>
  onSelectMethod: (method: string) => void
  isMethodsLoading: boolean
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs text-gray-500">
        {t('settlePayment.other.methodReceived')}
      </span>
      <div className="flex flex-wrap gap-2">
        {isMethodsLoading
          ? METHOD_SKELETON_WIDTHS.map((width, index) => (
              <MethodPillSkeleton key={index} width={width} />
            ))
          : methods.map((method) => (
              <MethodPill
                key={method}
                active={selectedMethod === method}
                recorded={recordedMethods.includes(method)}
                label={t(`adminPaymentMethods.typeLabel.${method}`)}
                method={method}
                onClick={() => onSelectMethod(method)}
              />
            ))}
      </div>
      {recordedMethods.length > 0 && (
        <span className="flex items-center gap-1.5 text-2xs text-gray-400 select-none">
          <span aria-hidden className="size-1.5 rounded-full bg-primary" />
          {t('settlePayment.other.recordedHint')}
        </span>
      )}
    </div>
  )
}

function AmountDateRow({
  amount,
  onAmountChange,
  amountOverLimit,
  chargeTotalLabel,
  date,
  onDateChange,
}: {
  amount: string
  onAmountChange: (next: string) => void
  amountOverLimit: boolean
  chargeTotalLabel: string
  date: Date
  onDateChange: (next: Date) => void
}) {
  const { t } = useTranslation()
  const today = useMemo(() => new Date(), [])
  return (
    <div className="flex w-full items-start gap-3">
      <div className="flex flex-1 flex-col gap-0.5">
        <Label size="sm">{t('settlePayment.amountReceived')}</Label>
        <Input
          inputSize="sm"
          mask="$"
          numberFormat
          error={amountOverLimit}
          placeholder="0.00"
          className="h-10 rounded-md px-2.5"
          value={amount}
          onChange={(e) => onAmountChange(e.target.value)}
        />
        {amountOverLimit && (
          <p className="text-xs text-red-500">
            {t('settlePayment.other.amountOverLimit', {
              total: chargeTotalLabel,
            })}
          </p>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-0.5">
        <Label size="sm">{t('settlePayment.dateReceived')}</Label>
        <DatePicker
          value={date}
          onValueChange={(next) => {
            if (next) onDateChange(next)
          }}
          dateFormat="MMM D, YYYY"
          instantApply
          toDate={today}
          className="h-10 rounded-md px-2.5"
        />
      </div>
    </div>
  )
}

export function OtherMethodForm({
  selectedMethod,
  onSelectMethod,
  recordedMethods,
  methods,
  isMethodsLoading,
  proofFiles,
  onAddProofFiles,
  onRemoveProofFile,
  amount,
  onAmountChange,
  amountOverLimit,
  chargeTotalLabel,
  date,
  onDateChange,
  reference,
  onReferenceChange,
}: OtherMethodFormProps) {
  const { t } = useTranslation()
  return (
    <div className="flex w-full flex-col gap-3">
      <MethodReceivedField
        methods={methods}
        selectedMethod={selectedMethod}
        recordedMethods={recordedMethods}
        onSelectMethod={onSelectMethod}
        isMethodsLoading={isMethodsLoading}
      />
      <ProofDropzone
        files={proofFiles}
        onAddFiles={onAddProofFiles}
        onRemoveFile={onRemoveProofFile}
      />
      <AmountDateRow
        amount={amount}
        onAmountChange={onAmountChange}
        amountOverLimit={amountOverLimit}
        chargeTotalLabel={chargeTotalLabel}
        date={date}
        onDateChange={onDateChange}
      />
      <div className="flex flex-col gap-0.5">
        <Label size="sm">{t('settlePayment.reference')}</Label>
        <Textarea
          textareaSize="sm"
          rows={1}
          placeholder={t('settlePayment.referenceOtherPlaceholder')}
          className="min-h-10"
          value={reference}
          onChange={(e) => onReferenceChange(e.target.value)}
        />
      </div>
    </div>
  )
}
